import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { getMe, createCheckoutSession, submitQuoteRequest } from "../api/apiClient";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button as MuiButton,
  CircularProgress,
} from "@mui/material";
import { getToken, clearSession } from "../api/session";
import {
  Sparkles,
  ShieldCheck,
  Check,
  CheckCircle2,
  Lock,
  Zap,
  Brain,
  Globe,
  Clock,
  FileCheck,
  ArrowRight,
  Play,
  ChevronDown,
  Layers,
  Search,
  Wrench,
  Upload,
  PenLine,
  Sparkle,
  Menu,
  X,
  FileText,
  Code,
  Users,
  Terminal,
  FileSpreadsheet,
  Boxes,
  Eye,
  Crosshair,
  ListChecks,
  FileDiff,
  GitCompareArrows,
} from "lucide-react";

/* ---------------- Hooks (all in this one file) ---------------- */

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "", style = {} }) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function useCountUp(target, start, duration = 1800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

/* ---------------- Page ---------------- */

function OnePassLanding() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      setShowStickyCta(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHeroMouse = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMouse({
      x: (e.clientX - rect.left - rect.width / 2) / rect.width,
      y: (e.clientY - rect.top - rect.height / 2) / rect.height,
    });
  };

  return (
    <div className="op-root">
      {/* ============ GLOBAL STYLES (single-file design system) ============ */}
      <style>{`
        .op-root {
          --op-heading: #001645;
          --op-blue: #0055FF;
          --op-accent: #00B6FF;
          --op-success: #22C55E;
          --op-bg: #F8FAFC;
          --op-card: #FFFFFF;
          --op-border: #E5E7EB;
          --op-grad: linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%);
          --op-grad-accent: linear-gradient(145deg,#00B6FF 0%,#0052D4 55%,#001A4A 100%);
          font-family: 'Inter', system-ui, sans-serif;
          background: var(--op-bg);
          color: var(--op-heading);
          overflow-x: hidden;
        }
        .op-root * { box-sizing: border-box; }
        .op-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        /* ---- animated background ---- */
        @keyframes op-blob {
          0%,100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(40px,-50px) scale(1.1); }
          66% { transform: translate(-30px,30px) scale(0.95); }
        }
        @keyframes op-blob2 {
          0%,100% { transform: translate(0,0) scale(1); }
          40% { transform: translate(-50px,40px) scale(1.08); }
          70% { transform: translate(35px,-25px) scale(0.92); }
        }
        .op-blob { position:absolute; border-radius:50%; filter:blur(90px); opacity:.35; pointer-events:none; will-change:transform; }
        .op-blob-a { animation: op-blob 18s ease-in-out infinite; }
        .op-blob-b { animation: op-blob2 22s ease-in-out infinite; }

        @keyframes op-particle {
          0% { transform: translateY(0); opacity:0; }
          20% { opacity:.7; }
          80% { opacity:.5; }
          100% { transform: translateY(-90vh); opacity:0; }
        }
        .op-particle {
          position:absolute; bottom:-10px; border-radius:50%;
          background: radial-gradient(circle,#00B6FF,#0055FF);
          animation: op-particle linear infinite; pointer-events:none;
          box-shadow: 0 0 8px rgba(0,182,255,.8);
        }

        .op-grid-bg {
          position:absolute; inset:0; pointer-events:none;
          background-image: linear-gradient(rgba(0,85,255,.04) 1px,transparent 1px),
            linear-gradient(90deg, rgba(0,85,255,.04) 1px, transparent 1px);
          background-size: 56px 56px;
          mask-image: radial-gradient(ellipse 80% 60% at 50% 20%, black 40%, transparent 100%);
        }

        /* ---- anchor targets ---- */
        /* The navbar is fixed, so jumping to a section would tuck its
           heading underneath. Offset each target by the nav's height. */
        html { scroll-behavior: smooth; }
        #features, #how-it-works, #why-onepass, #pricing, #get-started {
          scroll-margin-top: 104px;
        }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
        }

        /* ---- navbar ---- */
        .op-nav {
          position: fixed; top:0; left:0; right:0; z-index:50;
          transition: all .4s cubic-bezier(.22,1,.36,1);
        }
        .op-nav-inner {
          display:flex; align-items:center; justify-content:space-between;
          padding: 14px 28px; margin: 12px auto; max-width: 1200px;
          border-radius: 20px; transition: all .4s;
          background: rgba(255,255,255,.55);
          backdrop-filter: blur(6px) saturate(150%);
          border: 1px solid rgba(255,255,255,.6);
        }
        .op-nav.scrolled .op-nav-inner {
          background: rgba(255,255,255,.78);
          backdrop-filter: blur(20px) saturate(180%);
          box-shadow: 0 8px 32px rgba(0,22,69,.10);
          border-color: var(--op-border);
        }
        .op-nav-link {
          font-size:14px; font-weight:500; color:#3a4a6b; text-decoration:none;
          padding:8px 12px; border-radius:10px; transition: all .25s;
        }
        .op-nav-link:hover { color: var(--op-blue); background: rgba(0,85,255,.06); }

        /* ---- buttons ---- */
        .op-btn-primary {
          position:relative; overflow:hidden; cursor:pointer; border:none;
          background: var(--op-grad); color:#fff; font-family:inherit;
          font-weight:600; border-radius: 14px; display:inline-flex;
          align-items:center; gap:8px; justify-content:center;
          transition: transform .25s cubic-bezier(.22,1,.36,1), box-shadow .25s;
          box-shadow: 0 8px 24px rgba(0,85,255,.35);
        }
        .op-btn-primary:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 14px 40px rgba(0,85,255,.5);
        }
        .op-btn-primary:active { transform: translateY(0) scale(.98); }
        .op-btn-secondary {
          cursor:pointer; background:#fff; color:var(--op-heading);
          border:1px solid var(--op-border); font-family:inherit; font-weight:600;
          border-radius:14px; display:inline-flex; align-items:center; gap:8px;
          justify-content:center; transition: all .25s;
          box-shadow: 0 2px 10px rgba(0,22,69,.06);
        }
        .op-btn-secondary:hover {
          border-color: var(--op-accent); transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(0,182,255,.2);
        }
        @keyframes op-ripple {
          to { transform: scale(4); opacity: 0; }
        }
        .op-ripple {
          position:absolute; border-radius:50%; background: rgba(255,255,255,.5);
          transform: scale(0); animation: op-ripple .6s ease-out; pointer-events:none;
        }
        .op-sticky-cta {
          display: none;
        }

        /* ---- hero mockup ---- */
        .op-glass {
          background: rgba(255,255,255,.72);
          backdrop-filter: blur(24px) saturate(160%);
          border: 1px solid rgba(255,255,255,.85);
          box-shadow: 0 24px 60px rgba(0,22,69,.14), 0 2px 8px rgba(0,22,69,.06);
          border-radius: 20px;
        }
        @keyframes op-float {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        @keyframes op-float-s {
          0%,100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        .op-floating { animation: op-float 6s ease-in-out infinite; }
        .op-floating-s { animation: op-float-s 5s ease-in-out infinite; }
        @keyframes op-pulse-glow {
          0%,100% { box-shadow: 0 0 20px rgba(34,197,94,.3); }
          50% { box-shadow: 0 0 36px rgba(34,197,94,.6); }
        }
        @keyframes op-shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }

        /* ---- scroll indicator ---- */
        @keyframes op-bounce { 0%,100% { transform: translateY(0);} 50% { transform: translateY(10px);} }
        @keyframes op-wheel { 0% { opacity:1; transform: translateY(0);} 100% { opacity:0; transform: translateY(10px);} }
        .op-mouse {
          width:26px; height:42px; border:2px solid #94a3c4; border-radius:14px;
          display:flex; justify-content:center; padding-top:7px;
          animation: op-bounce 2s ease-in-out infinite;
        }
        .op-mouse::after {
          content:''; width:4px; height:8px; border-radius:4px; background: var(--op-blue);
          animation: op-wheel 1.6s ease-in-out infinite;
        }

        @keyframes op-icon-wave {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-7px) scale(1.04); }
        }
        .op-steps-line {
          position: absolute;
        }
        .op-steps-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          align-items: stretch;
        }
        .op-steps-grid > div {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .op-step-card {
          background: #fff;
          border: 1px solid var(--op-border);
          border-radius: 20px;
          padding: 28px 16px 24px;
          text-align: center;
          transition: all .35s cubic-bezier(.22,1,.36,1);
          position: relative;
          z-index: 1;
          height: 100%;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          box-sizing: border-box;
        }
        .op-step-card::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 20px;
          padding: 1.5px;
          background: linear-gradient(135deg, rgba(0,182,255,.6), rgba(0,85,255,.25), rgba(34,197,94,.4));
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity .35s;
          pointer-events: none;
        }
        .op-step-card:hover::before {
          opacity: 1;
        }
        .op-step-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 44px rgba(0,85,255,.15);
          border-color: rgba(0,182,255,.5);
        }
        .op-step-icon-wrap {
          animation: op-icon-wave 3.8s ease-in-out infinite;
        }
        .op-step-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .op-step-title {
          font-weight: 700;
          font-size: 15px;
          margin: 6px 0 6px;
          color: #001645;
          min-height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1.25;
        }
        .op-step-desc {
          font-size: 12.5px;
          color: #6a7a98;
          line-height: 1.45;
          min-height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: auto;
        }
        @media (max-width: 768px) {
          .op-step-card {
            padding: 14px 14px !important;
            border-radius: 14px !important;
            display: flex !important;
            flex-direction: row !important;
            align-items: center !important;
            gap: 12px !important;
            text-align: left !important;
            height: 100% !important;
          }
          .op-step-card:hover { transform: translateY(-3px); }
          .op-step-icon-wrap {
            flex-shrink: 0;
            width: 38px !important;
            height: 38px !important;
            border-radius: 11px !important;
            margin: 0 !important;
          }
          .op-step-text {
            flex: 1 !important;
            min-width: 0 !important;
            display: block !important;
          }
          .op-step-title {
            font-size: 13px !important;
            margin: 2px 0 3px !important;
            min-height: auto !important;
            display: block !important;
            text-align: left !important;
          }
          .op-step-desc {
            font-size: 12px !important;
            min-height: auto !important;
            display: block !important;
            text-align: left !important;
          }
        }
        @keyframes op-line-draw { from { transform: scaleX(0);} to { transform: scaleX(1);} }

        /* ---- feature cards ---- */
        .op-feature {
          position:relative; background:#fff; border-radius:20px; padding:34px 28px;
          border:1px solid var(--op-border);
          transition: transform .35s cubic-bezier(.22,1,.36,1), box-shadow .35s;
        }
        .op-feature::before {
          content:''; position:absolute; inset:0; border-radius:20px; padding:1.5px;
          background: var(--op-grad-accent);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          opacity:0; transition: opacity .35s;
        }
        .op-feature:hover { transform: translateY(-8px); box-shadow: 0 24px 56px rgba(0,22,69,.12); }
        .op-feature:hover::before { opacity:1; }
        .op-feature:hover .op-feature-icon { transform: scale(1.1) rotate(-4deg); }
        .op-feature-icon {
          width:56px; height:56px; border-radius:16px; display:flex; align-items:center;
          justify-content:center; background: linear-gradient(145deg, rgba(0,182,255,.14), rgba(0,85,255,.14));
          color: var(--op-blue); transition: transform .35s;
        }
        @media (max-width: 768px) {
          .op-feature {
            padding: 20px 18px !important;
            border-radius: 16px !important;
            display: flex;
            align-items: flex-start;
            gap: 14px;
          }
          .op-feature-icon {
            width: 42px !important; height: 42px !important;
            border-radius: 12px !important; flex-shrink: 0;
          }
          .op-feature h3 { font-size: 15px !important; margin: 0 0 6px !important; }
          .op-feature p  { font-size: 13px !important; }
        }        /* ---- logo cloud ---- */
        .op-logo-item {
          font-weight:700; font-size:15px; color:#64748b;
          display:flex; align-items:center; gap:10px; transition: all .3s; opacity:.8;
        }
        .op-logo-item:hover { color:#1e293b; opacity:1; transform: translateY(-2px); }

        /* ---- demo typing ---- */
        @keyframes op-caret { 0%,100%{opacity:1;} 50%{opacity:0;} }
        .op-caret { display:inline-block; width:2px; height:1em; background:var(--op-blue);
          vertical-align:text-bottom; animation: op-caret 1s step-end infinite; }
        @keyframes op-pop { 0%{transform:scale(.5); opacity:0;} 70%{transform:scale(1.15);} 100%{transform:scale(1); opacity:1;} }
        .op-pop { animation: op-pop .45s cubic-bezier(.22,1,.36,1) both; }

        /* ---- checklist ---- */
        @keyframes op-check-in { from { opacity:0; transform: translateX(-18px);} to { opacity:1; transform:translateX(0);} }

        /* ---- testimonial ---- */
        .op-dot { width:8px; height:8px; border-radius:50%; background:#cbd5e1; border:none; cursor:pointer; transition:all .3s; padding:0; }
        .op-dot.active { background: var(--op-blue); width:24px; border-radius:6px; }

        /* ---- gradient anim ---- */
        @keyframes op-grad-move { 0%,100%{background-position:0% 50%;} 50%{background-position:100% 50%;} }
        .op-grad-anim { background-size: 200% 200%; animation: op-grad-move 10s ease infinite; }

        /* ---- sticky mobile cta ---- */
        .op-sticky-cta { display:none; }
        .op-menu-icon { display: none; }
        /* ---- mobile menu drawer ---- */
        .op-mobile-menu-backdrop {
          display: none;
          position: fixed; inset: 0; z-index: 99;
          background: rgba(0, 22, 69, 0.35);
          backdrop-filter: blur(4px);
          opacity: 0;
          transition: opacity .35s cubic-bezier(.22,1,.36,1);
        }
        .op-mobile-menu-backdrop.open { opacity: 1; }

        .op-mobile-menu {
          display: none;
          position: fixed; top: 0; left: 0; bottom: 0;
          width: min(320px, 88vw);
          z-index: 100;
          flex-direction: column;
          background: rgba(255,255,255,.92);
          backdrop-filter: blur(28px) saturate(180%);
          border-left: 1px solid rgba(255,255,255,.75);
          box-shadow: -12px 0 56px rgba(0,22,69,.18);
          transform: translateX(100%);
          transition: transform .38s cubic-bezier(.22,1,.36,1);
          overflow-y: auto;
        }
        .op-mobile-menu.open { display: flex !important; transform: translateX(0); }

        .op-mobile-menu-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 22px 18px;
          border-bottom: 1px solid var(--op-border);
        }
        .op-mobile-menu-close {
          width: 36px; height: 36px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,22,69,.06); border: none; cursor: pointer;
          color: #001645; transition: background .2s;
        }
        .op-mobile-menu-close:hover { background: rgba(0,85,255,.12); color: var(--op-blue); }

        .op-mobile-nav-links {
          display: flex; flex-direction: column;
          padding: 16px 16px 8px;
          gap: 2px;
        }
        .op-mobile-nav-link {
          display: flex; align-items: center; gap: 10px;
          font-size: 15px; font-weight: 500; color: #3a4a6b;
          text-decoration: none; padding: 13px 14px;
          border-radius: 12px; transition: all .2s;
        }
        .op-mobile-nav-link:hover {
          color: var(--op-blue); background: rgba(0,85,255,.07);
        }
        .op-mobile-nav-divider {
          height: 1px; background: var(--op-border);
          margin: 8px 16px;
        }
        .op-mobile-menu-actions {
          display: flex; flex-direction: column;
          padding: 8px 16px 24px;
          gap: 10px;
        }
        @media (max-width: 789px) {
          .op-menu-icon { display: flex !important; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; background: rgba(0,22,69,.06); border: none; cursor: pointer; color: #001645; padding: 0; transition: background .2s; }
          .op-menu-icon:hover { background: rgba(0,85,255,.1); color: var(--op-blue); }
          .op-nav-links { display:none !important; }
          .op-nav-buttons { display: none !important; }
          .op-mobile-menu-backdrop.open { display: block !important; }
        }
        @media (max-width: 768px) {
          .op-hero-grid { grid-template-columns: 1fr !important; gap:48px !important; }
          .op-hero-h1 { font-size: 44px !important; }
          .op-steps-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .op-steps-line { display:none !important; }
          .op-features-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .op-spe-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .op-spe-card { padding: 26px 20px !important; }
          .op-split { grid-template-columns: 1fr !important; }
          .op-stats-grid { grid-template-columns: 1fr 1fr !important; }
          /* section padding */
          #how-it-works, #why-onepass, #features { padding: 56px 0 !important; }
          /* SectionHeading */
          #how-it-works h2, #why-onepass h2, #features h2 { font-size: 28px !important; margin-bottom: 8px !important; }
          #how-it-works > div > div:first-child, #why-onepass > div > div:first-child { margin-bottom: 36px !important; }
          /* demo section */
          .op-demo-chrome { padding: 10px 14px !important; }
          .op-demo-body { padding: 20px 16px !important; min-height: auto !important; }
          .op-demo-blueprint { grid-template-columns: 1fr !important; gap: 8px !important; }
          .op-package-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
          .op-audience-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .op-demo-actions { gap: 10px !important; }
          .op-demo-actions span { font-size: 13px !important; padding: 9px 14px !important; }
          .op-sticky-cta {
            display:flex; position:fixed; bottom:16px; left:16px; right:16px; z-index:60;
            padding:16px; font-size:16px;
          }
          .op-footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 520px) {
          .op-steps-grid { grid-template-columns: 1fr !important; gap: 8px !important; }
          .op-features-grid { grid-template-columns: 1fr !important; gap: 8px !important; }
          .op-spe-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
          .op-stats-grid { grid-template-columns: 1fr 1fr !important; }
          .op-nav-inner { padding: 10px 16px !important; }
          .op-nav-btn { padding: 6px 10px !important; font-size: 12px !important; gap: 4px !important; }
          #how-it-works, #why-onepass, #features { padding: 44px 0 !important; }
          .op-demo-body { padding: 16px 12px !important; }
          .op-demo-blueprint { gap: 6px !important; }
        }
      `}</style>

      <Navbar scrolled={scrolled} />

      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        onMouseMove={onHeroMouse}
        style={{
          position: "relative",
          paddingTop: 150,
          paddingBottom: 80,
          overflow: "hidden",
        }}
      >
        {/* background */}
        <div className="op-grid-bg" />
        <div
          className="op-blob op-blob-a"
          style={{
            width: 480,
            height: 480,
            top: -120,
            left: -140,
            background: "#0055FF",
          }}
        />
        <div
          className="op-blob op-blob-b"
          style={{
            width: 420,
            height: 420,
            top: 80,
            right: -120,
            background: "#00B6FF",
          }}
        />
        <div
          className="op-blob op-blob-a"
          style={{
            width: 360,
            height: 360,
            bottom: -100,
            left: "38%",
            background: "#0D47A1",
            animationDelay: "-8s",
          }}
        />
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="op-particle"
            style={{
              left: `${(i * 7.3 + 4) % 100}%`,
              width: 3 + (i % 3) * 2,
              height: 3 + (i % 3) * 2,
              animationDuration: `${9 + (i % 5) * 3}s`,
              animationDelay: `${(i * 1.7) % 10}s`,
            }}
          />
        ))}

        <div
          className="op-container op-hero-grid"
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "1.05fr 1fr",
            gap: 64,
            alignItems: "center",
          }}
        >
          {/* LEFT */}
          <div>
            <Reveal>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(0,85,255,.08)",
                  border: "1px solid rgba(0,85,255,.18)",
                  color: "#0055FF",
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: 999,
                }}
              >
                <Sparkles size={14} /> Blueprint Intelligence Platform
              </span>
            </Reveal>
            <Reveal delay={100}>
              <h1
                className="op-hero-h1"
                style={{
                  fontSize: 68,
                  lineHeight: 1.04,
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  color: "#001645",
                  margin: "26px 0 0",
                }}
              >
                Fix it before
                <br />
                <span
                  style={{
                    background:
                      "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#00B6FF 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  you build it.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p
                style={{
                  fontSize: 18.5,
                  lineHeight: 1.6,
                  color: "#334155",
                  fontWeight: 600,
                  maxWidth: 540,
                  margin: "24px 0 0",
                }}
              >
                OnePass Blueprint helps both sides of a project — the people defining what they want and the people responsible for building it.
              </p>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: "#4a5878",
                  maxWidth: 540,
                  margin: "12px 0 0",
                }}
              >
                Developers can give OnePass to clients to create a clearer project brief. Project owners can give the blueprint to developers and designers so they know exactly what needs to be built.
              </p>
              <p
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: "#64748b",
                  maxWidth: 540,
                  margin: "12px 0 0",
                }}
              >
                Better understanding upfront can save time, reduce back-and-forth and rework, and reduce unnecessary development and AI costs.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  marginTop: 36,
                  flexWrap: "wrap",
                }}
              >
                <RippleButton
                  onClick={() => navigate("/build")}
                  className="op-btn-primary op-grad-anim"
                  style={{
                    padding: "17px 32px",
                    fontSize: 16,
                    background:
                      "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                  }}
                >
                  Try OnePass Free <ArrowRight size={18} />
                </RippleButton>
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px 22px",
                  marginTop: 32,
                }}
              >
                {["Free for 30 Days", "Privacy First", "Export Anywhere"].map(
                  (t) => (
                    <span
                      key={t}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: "#5a6a8a",
                      }}
                    >
                      <Check size={15} color="#22C55E" strokeWidth={3} /> {t}
                    </span>
                  ),
                )}
              </div>
            </Reveal>
          </div>

          {/* RIGHT — floating dashboard mockup */}
          <Reveal delay={250}>
            <div
              style={{
                position: "relative",
                transform: `perspective(1200px) rotateY(${mouse.x * -6}deg) rotateX(${mouse.y * 6}deg)`,
                transition: "transform .3s ease-out",
              }}
            >
              {/* glow */}
              <div
                style={{
                  position: "absolute",
                  inset: "10% 5%",
                  background:
                    "radial-gradient(circle,rgba(0,85,255,.28),transparent 70%)",
                  filter: "blur(50px)",
                }}
              />

              {/* main card */}
              <div
                className="op-glass op-floating"
                style={{ position: "relative", padding: 26 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 22,
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    <LogoMark size={22} />
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#22C55E",
                      background: "rgba(34,197,94,.1)",
                      padding: "4px 10px",
                      borderRadius: 999,
                    }}
                  >
                    ● Validated
                  </span>
                </div>
                {/* stepper */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 24,
                  }}
                >
                  {[
                    "Describe",
                    "Validate",
                    "Fix",
                    "Lock",
                    "Blueprint",
                    "Export",
                  ].map((s, i) => (
                    <div key={s} style={{ flex: 1, textAlign: "center" }}>
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          background:
                            i < 3
                              ? "linear-gradient(145deg,#00B6FF,#0052D4)"
                              : i === 3
                                ? "#0055FF"
                                : "#e8edf6",
                          color: i <= 3 ? "#fff" : "#8a9ab8",
                          boxShadow:
                            i === 3 ? "0 0 0 5px rgba(0,85,255,.15)" : "none",
                        }}
                      >
                        {i < 3 ? <Check size={13} strokeWidth={3} /> : i + 1}
                      </div>
                      <div
                        style={{
                          fontSize: 9.5,
                          marginTop: 5,
                          fontWeight: 600,
                          color: i <= 3 ? "#0055FF" : "#9aa8c4",
                        }}
                      >
                        {s}
                      </div>
                    </div>
                  ))}
                </div>
                {/* blueprint rows */}
                {[92, 74, 84].map((w, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#f2f6fc",
                      borderRadius: 12,
                      padding: "12px 14px",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <FileCheck size={16} color="#0055FF" />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          height: 7,
                          width: `${w}%`,
                          borderRadius: 4,
                          background: "linear-gradient(90deg,#c7d8f7,#dbe7fa)",
                        }}
                      />
                      <div
                        style={{
                          height: 5,
                          width: `${w - 25}%`,
                          borderRadius: 4,
                          background: "#e8eef8",
                          marginTop: 6,
                        }}
                      />
                    </div>
                    <CheckCircle2 size={16} color="#22C55E" />
                  </div>
                ))}
              </div>

              {/* floating validation card */}
              <div
                className="op-glass op-floating-s"
                style={{
                  position: "absolute",
                  top: -26,
                  right: -14,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderRadius: 16,
                  animationDelay: "-2s",
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: "rgba(34,197,94,.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "op-pulse-glow 3s infinite",
                  }}
                >
                  <ShieldCheck size={18} color="#22C55E" />
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                    Validation Passed
                  </div>
                  <div style={{ fontSize: 10.5, color: "#7a8aa8" }}>
                    0 conflicts found
                  </div>
                </div>
              </div>

              {/* floating lock card */}
              <div
                className="op-glass op-floating-s"
                style={{
                  position: "absolute",
                  bottom: -22,
                  left: -18,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderRadius: 16,
                  animationDelay: "-4s",
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: "rgba(0,85,255,.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Lock size={17} color="#0055FF" />
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                    Blueprint Locked
                  </div>
                  <div style={{ fontSize: 10.5, color: "#7a8aa8" }}>
                    Source of truth ready
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* scroll indicator */}
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 72 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div className="op-mouse" />
            <ChevronDown size={16} color="#94a3c4" />
          </div>
        </div>
      </section>

      {/* ============ TRUST / LOGO CLOUD ============ */}
      <section style={{ padding: "40px 0 72px" }}>
        <div className="op-container">
          <Reveal>
            <p
              style={{
                textAlign: "center",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#8a9ab8",
                marginBottom: 32,
              }}
            >
              Trusted by teams building with
            </p>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "24px 56px",
                alignItems: "center",
              }}
            >
              {["Cursor", "Claude", "Codex", "GitHub", "OpenAI", "Vercel"].map(
                (n) => (
                  <span key={n} className="op-logo-item">
                    <LogoGlyph name={n} /> {n}
                  </span>
                ),
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ TESTIMONIAL ============ */}
      <section style={{ padding: "0 0 52px", position: "relative" }}>
        <div className="op-container" style={{ maxWidth: 840 }}>
          <Reveal>
            <div
              className="op-glass"
              style={{
                borderRadius: 20,
                padding: "26px 34px",
                textAlign: "center",
                background: "rgba(255, 255, 255, 0.85)",
                border: "1px solid rgba(229, 231, 235, 0.9)",
                boxShadow: "0 8px 30px rgba(0, 22, 69, 0.04)",
              }}
            >
              <p
                style={{
                  fontSize: 16.5,
                  lineHeight: 1.65,
                  color: "#1e293b",
                  fontStyle: "italic",
                  margin: 0,
                }}
              >
                “I knew what I wanted to build, but I was having trouble clearly explaining it to my developer. OnePass Blueprint turned my idea into a clear blueprint he could understand and work from.”
              </p>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0055FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span>— Kacey Thorner</span>
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>•</span>
                <span style={{ color: "#64748b", fontWeight: 500 }}>Owner, Kacey Cares</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <HowItWorks />

      {/* ============ SECOND PAIR OF EYES (after Step 6 — Export) ============ */}
      <SecondPairOfEyes />

      {/* ============ WHY ONEPASS ============ */}
      <WhyOnePass />

      {/* ============ INTERACTIVE DEMO ============ */}
      <InteractiveDemo />

      {/* ============ BUILD PROTECTION ============ */}
      {/* <BuildProtection /> */}

      {/* ============ STATISTICS ============ */}
      {/* <Statistics /> */}

      {/* ============ TESTIMONIALS ============ */}
      {/* <Testimonials /> */}

      {/* ============ PricingSection ============ */}
      <PricingSection />

      {/* ============ FINAL CTA ============ */}
      <FinalCTA />

      {/* ============ DISCLAIMER ============ */}
      {/* <DisclaimerSection /> */}

      {/* ============ FOOTER ============ */}
      <Footer />

      {/* sticky mobile CTA - only shown after scrolling past initial hero button */}
      {showStickyCta && (
        <RippleButton
          onClick={() => navigate("/build")}
          className="op-btn-primary op-sticky-cta op-grad-anim"
          style={{
            background:
              "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
          }}
        >
          Try OnePass Free <ArrowRight size={18} />
        </RippleButton>
      )}
    </div>
  );
}

/* ---------------- Sections (still one file, just organized) ---------------- */

function LogoMark() {
  return (
    <img
      src="./v_two_white.png"
      alt="OnePass Logo"
      style={{
        width: "160px",
        height: "auto",
        objectFit: "contain",
        display: "block",
        filter: "drop-shadow(0 4px 10px rgba(0,85,255,.25))",
        borderRadius: "10%",
      }}
    />
  );
}

function LogoGlyph({ name }) {
  const map = {
    Cursor: (
      // Cursor logo — the "C" wordmark style
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#000" />
        <path d="M7 5l5 14M7 5h10M12 12h5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    Claude: (
      // Anthropic / Claude logo mark
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#D97757" />
        <path d="M12 4.5L16.5 16H14l-1-3H11l-1 3H7.5L12 4.5z" fill="#fff" />
        <path d="M11.4 11.5h1.2l-.6-2-.6 2z" fill="#D97757" />
      </svg>
    ),
    Codex: (
      // OpenAI Codex — similar to OpenAI mark
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#10A37F" />
        <path d="M12 3.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zm0 2a6.5 6.5 0 110 13 6.5 6.5 0 010-13z" fill="#fff" fillOpacity=".3" />
        <path d="M9 9l1.5 3L12 9l1.5 3L15 9" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.5 13.5h7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    GitHub: (
      // Official GitHub Octocat mark (simplified)
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#24292F" />
        <path fillRule="evenodd" clipRule="evenodd" d="M12 4.8a7.2 7.2 0 00-2.277 14.03c.36.066.492-.156.492-.347 0-.17-.006-.621-.01-1.22-2.003.435-2.426-.966-2.426-.966-.327-.832-.8-1.053-.8-1.053-.653-.447.05-.438.05-.438.722.05 1.102.742 1.102.742.642 1.1 1.684.782 2.094.598.065-.465.251-.782.456-.962-1.598-.182-3.277-.799-3.277-3.557 0-.786.28-1.428.742-1.932-.075-.181-.322-.913.07-1.903 0 0 .604-.194 1.98.737A6.894 6.894 0 0112 9.52c.612.003 1.228.083 1.803.242 1.374-.931 1.977-.737 1.977-.737.393.99.147 1.722.072 1.903.463.504.74 1.146.74 1.932 0 2.765-1.682 3.373-3.285 3.551.258.223.488.662.488 1.335 0 .964-.009 1.74-.009 1.977 0 .193.13.417.496.347A7.2 7.2 0 0012 4.8z" fill="#fff" />
      </svg>
    ),
    OpenAI: (
      // OpenAI logo mark
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#000" />
        <path d="M20.49 9.42a5.3 5.3 0 00-.46-4.36 5.4 5.4 0 00-5.82-2.59A5.3 5.3 0 0010.2 1a5.4 5.4 0 00-5.15 3.74 5.3 5.3 0 00-3.54 2.56 5.4 5.4 0 00.67 6.34 5.3 5.3 0 00.46 4.36 5.4 5.4 0 005.82 2.59A5.3 5.3 0 0012.8 22a5.4 5.4 0 005.15-3.74 5.3 5.3 0 003.54-2.56 5.4 5.4 0 00-.99-6.28h-.01zM12.8 20.52a4 4 0 01-2.57-.94l.13-.07 4.27-2.47a.71.71 0 00.35-.61v-6.02l1.8 1.04a.07.07 0 01.04.05v4.99a4.03 4.03 0 01-4.02 4.03zm-8.65-3.69a4 4 0 01-.48-2.7l.13.08 4.27 2.47c.22.13.49.13.71 0l5.21-3.01v2.08a.07.07 0 01-.03.06L9.6 18.24a4.03 4.03 0 01-5.45-1.41zm-1.12-9.34A4 4 0 015.1 5.77v5.08a.7.7 0 00.35.61l5.21 3.01-1.8 1.04a.07.07 0 01-.07 0L4.4 12.97a4.03 4.03 0 01-.37-5.48zm14.81 3.46l-5.21-3.01 1.8-1.04a.07.07 0 01.07 0l4.39 2.54a4.02 4.02 0 01-.62 7.26v-5.08a.7.7 0 00-.43-.67zm1.79-2.72l-.13-.08-4.27-2.46a.71.71 0 00-.71 0L9.3 9.7V7.62a.07.07 0 01.03-.06l4.39-2.54a4.02 4.02 0 016.16 4.16l-.05.05zm-11.3 3.72l-1.8-1.04a.07.07 0 01-.04-.05V6.87a4.02 4.02 0 016.6-3.08l-.13.07-4.27 2.47a.71.71 0 00-.35.61l-.01 6.01zm.98-2.12l2.32-1.34 2.32 1.34v2.67l-2.32 1.34-2.32-1.34V11.83z" fill="white" />
      </svg>
    ),
    Vercel: (
      // Vercel triangle logo
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="6" fill="#000" />
        <path d="M12 5l8.5 14H3.5L12 5z" fill="#fff" />
      </svg>
    ),
  };
  return <>{map[name]}</>;
}

function RippleButton({ children, className, style, onClick }) {
  const ref = useRef(null);
  const handle = (e) => {
    const btn = ref.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const d = Math.max(rect.width, rect.height);
      const span = document.createElement("span");
      span.className = "op-ripple";
      span.style.width = span.style.height = `${d}px`;
      span.style.left = `${e.clientX - rect.left - d / 2}px`;
      span.style.top = `${e.clientY - rect.top - d / 2}px`;
      btn.appendChild(span);
      setTimeout(() => span.remove(), 650);
    }
    onClick?.();
  };
  return (
    <button ref={ref} className={className} style={style} onClick={handle}>
      {children}
    </button>
  );
}

function Navbar({ scrolled }) {
  const navigate = useNavigate();
  const token = getToken();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    clearSession();
    navigate("/build/login");
  };

  return (
    <nav
      className={`op-nav ${scrolled ? "scrolled" : ""}`}
      style={{ justifyContent: "center", display: "flex" }}
    >
      <div
        className="op-nav-inner"
        style={{
          paddingLeft: 24,
          paddingRight: 16,
          marginLeft: 16,
          marginRight: 16,
          width: "100%",
        }}
      >
        <a
          href="#"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
            color: "#001645",
          }}
        >
          <LogoMark />
          {/* <span
            style={{ fontWeight: 800, fontSize: 19, letterSpacing: "-0.02em" }}
          >
            OnePass
          </span> */}
        </a>
        <div
          className="op-nav-links"
          style={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          {[
            "Features",
            "How it Works",
            "Why OnePass",
            "Pricing",
          ].map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase().replace(/\s/g, "-")}`}
              className="op-nav-link"
            >
              {l}
            </a>
          ))}
          <Link to="/legal" className="op-nav-link">Legal</Link>
        </div>
        <div className="op-nav-buttons" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {token ? (
            <>
              <button
                onClick={handleLogout}
                className="op-btn-secondary op-nav-btn"
                style={{ padding: "11px 22px", fontSize: 14 }}
              >
                Logout
              </button>
              <RippleButton
                onClick={() => navigate("/build")}
                className="op-btn-primary op-grad-anim op-nav-btn"
                style={{
                  padding: "11px 22px",
                  fontSize: 14,
                  gap: 6,
                  background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                }}
              >
                Build Code <ArrowRight size={16} />
              </RippleButton>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/build/login")}
                className="op-btn-secondary op-nav-btn"
                style={{ padding: "11px 22px", fontSize: 14 }}
              >
                Sign In
              </button>
              <RippleButton
                onClick={() => navigate("/build/register")}
                className="op-btn-primary op-grad-anim op-nav-btn"
                style={{
                  padding: "11px 22px",
                  fontSize: 14,
                  gap: 6,
                  background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                }}
              >
                Sign Up <ArrowRight size={16} />
              </RippleButton>
            </>
          )}
        </div>

        {/* Mobile Menu Icon */}
        <button
          className="op-menu-icon"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu size={26} />
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`op-mobile-menu-backdrop${mobileMenuOpen ? " open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Drawer */}
      <div className={`op-mobile-menu${mobileMenuOpen ? " open" : ""}`}>
        {/* Drawer Header — matches op-nav-inner branding */}
        <div className="op-mobile-menu-header">
          <a
            href="#"
            style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "#001645" }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <LogoMark />
            {/* <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em" }}>OnePass</span> */}
          </a>
          <button
            className="op-mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav Links */}
        <nav className="op-mobile-nav-links">
          {[
            { label: "Features", icon: <Zap size={16} /> },
            { label: "How it Works", icon: <Layers size={16} /> },
            { label: "Why OnePass", icon: <ShieldCheck size={16} /> },
            { label: "Pricing", icon: <CheckCircle2 size={16} /> },
          ].map(({ label, icon }) => (
            <a
              key={label}
              href={`#${label.toLowerCase().replace(/\s/g, "-")}`}
              className="op-mobile-nav-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span style={{ color: "var(--op-blue)", opacity: 0.75 }}>{icon}</span>
              {label}
            </a>
          ))}
          <Link
            to="/legal"
            className="op-mobile-nav-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span style={{ color: "var(--op-blue)", opacity: 0.75 }}><Lock size={16} /></span>
            Legal
          </Link>
        </nav>

        <div className="op-mobile-nav-divider" />

        {/* CTA Buttons */}
        <div className="op-mobile-menu-actions">
          {token ? (
            <>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/build"); }}
                className="op-btn-primary op-grad-anim"
                style={{
                  padding: "14px 22px", fontSize: 15, width: "100%",
                  background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                }}
              >
                Build Code <ArrowRight size={17} />
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                className="op-btn-secondary"
                style={{ padding: "13px 22px", fontSize: 15, width: "100%" }}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <RippleButton
                onClick={() => { setMobileMenuOpen(false); navigate("/build/register"); }}
                className="op-btn-primary op-grad-anim"
                style={{
                  padding: "14px 22px", fontSize: 15, width: "100%",
                  background: "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
                }}
              >
                Sign Up <ArrowRight size={17} />
              </RippleButton>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/build/login"); }}
                className="op-btn-secondary"
                style={{ padding: "13px 22px", fontSize: 15, width: "100%" }}
              >
                Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <Reveal>
      <div
        style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 64px" }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#0055FF",
          }}
        >
          {eyebrow}
        </span>
        <h2
          style={{
            fontSize: 42,
            fontWeight: 800,
            letterSpacing: "-0.025em",
            color: "#001645",
            margin: "14px 0 0",
            lineHeight: 1.15,
          }}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            style={{
              fontSize: 17,
              color: "#5a6a8a",
              marginTop: 16,
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
    </Reveal>
  );
}

function HowItWorks() {
  const { ref, visible } = useReveal(0.2);
  const steps = [
    { icon: <PenLine size={22} />, title: "Describe", desc: "Type your idea." },

    {
      icon: <Search size={22} />,
      title: "Validate",
      desc: "Detect missing logic.",
    },
    {
      icon: <Wrench size={22} />,
      title: "Fix",
      desc: "Resolve issues before building.",
    },
    {
      icon: <Lock size={22} />,
      title: "Lock",
      desc: "Create the source of truth.",
    },
    {
      icon: <Layers size={22} />,
      title: "Generate Blueprint",
      desc: "OnePass structures everything.",
    },
    {
      icon: <Upload size={22} />,
      title: "Finalize & Export",
      desc: "Human Package + Developer Package",
    },
  ];
  return (
    <section
      id="how-it-works"
      style={{ padding: "80px 0", position: "relative", overflow: "hidden" }}
    >
      {/* ambient wave ribbon */}
      <div
        style={{
          position: "absolute",
          top: "40%",
          left: "-10%",
          right: "-10%",
          height: 120,
          background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,182,255,.08), transparent 70%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <div className="op-container" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="How it works"
          title="From idea to validated blueprint"
          subtitle="Six simple steps. One clean pass. Zero wasted builds."
        />
        <div ref={ref} style={{ position: "relative" }}>
          {/* connector line */}
          <div
            className="op-steps-line"
            style={{
              position: "absolute",
              top: 54,
              left: "6%",
              right: "6%",
              height: 3,
              borderRadius: 3,
              background: "linear-gradient(90deg,#00B6FF,#0055FF,#0D47A1)",
              zIndex: 0,
            }}
          />
          <div
            className="op-steps-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 16,
            }}
          >
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 120} style={{ height: "100%" }}>
                <div className="op-step-card">
                  <div
                    className="op-step-icon-wrap"
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 16,
                      margin: "0 auto 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(145deg,#00B6FF,#0052D4)",
                      color: "#fff",
                      boxShadow: "0 8px 20px rgba(0,85,255,.3)",
                      flexShrink: 0,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  >
                    {s.icon}
                  </div>
                  <div className="op-step-text">
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#00A3E6",
                        letterSpacing: "0.08em",
                      }}
                    >
                      STEP {i + 1}
                    </div>
                    <div className="op-step-title">
                      {s.title}
                    </div>
                    <div className="op-step-desc">
                      {s.desc}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SecondPairOfEyes() {
  // The client's four-beat promise, one tile each.
  const outcomes = [
    {
      icon: <ListChecks size={24} />,
      title: "See what matches",
      desc: "Every approved requirement your build already satisfies.",
    },
    {
      icon: <FileDiff size={24} />,
      title: "Find what doesn’t",
      desc: "Where the coding output drifted from what was intended.",
    },
    {
      icon: <Crosshair size={24} />,
      title: "Know where to look",
      desc: "The functional area to check first — and why.",
    },
    {
      icon: <GitCompareArrows size={24} />,
      title: "Know what needs to change",
      desc: "A focused Correction Blueprint, not a full rebuild.",
    },
  ];
  const assurances = [
    { icon: <Lock size={14} />, label: "Your code stays with you" },
    { icon: <Eye size={14} />, label: "No full repository access required" },
    { icon: <CheckCircle2 size={14} />, label: "Included free with OnePass" },
  ];

  return (
    <section
      id="second-pair-of-eyes"
      style={{ padding: "80px 0", position: "relative", overflow: "hidden" }}
    >
      <div
        className="op-blob op-blob-a"
        style={{
          width: 420,
          height: 420,
          top: -160,
          left: -180,
          background: "#0055FF",
          opacity: 0.14,
        }}
      />
      <div className="op-container" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="Step 7 · Second Pair of Eyes"
          title="Blueprint It. Build It. Know Where to Look."
          subtitle="Something not working as expected?"
        />

        <Reveal delay={100}>
          <div
            className="op-glass op-spe-card"
            style={{
              maxWidth: 860,
              margin: "-24px auto 40px",
              padding: "34px 38px",
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontSize: 17.5,
                fontWeight: 600,
                color: "#001645",
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              Second Pair of Eyes compares your coding output to your approved
              OnePass Blueprint to see whether what was built matches what was
              intended.
            </p>
            <p style={{ fontSize: 15.5, color: "#5a6a8a", lineHeight: 1.7, margin: "14px 0 0" }}>
              If something doesn’t match, it helps pinpoint where the issue is
              and why, then creates a focused Correction Blueprint showing what
              needs to be corrected.
            </p>
            <p style={{ fontSize: 15.5, color: "#5a6a8a", lineHeight: 1.7, margin: "14px 0 0" }}>
              Spend less time searching through code, rerunning prompts, and
              burning tokens trying to find what went wrong.
            </p>
          </div>
        </Reveal>

        <div
          className="op-spe-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 18 }}
        >
          {outcomes.map((o, i) => (
            <Reveal key={o.title} delay={i * 110} style={{ height: "100%" }}>
              <div className="op-feature" style={{ height: "100%", padding: "26px 22px" }}>
                <div className="op-feature-icon" style={{ width: 48, height: 48, borderRadius: 14 }}>
                  {o.icon}
                </div>
                <h3
                  style={{
                    fontSize: 16.5,
                    fontWeight: 700,
                    color: "#001645",
                    margin: "16px 0 6px",
                  }}
                >
                  {o.title}
                </h3>
                <p style={{ fontSize: 14, color: "#5a6a8a", lineHeight: 1.6, margin: 0 }}>
                  {o.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 10,
              marginTop: 34,
            }}
          >
            {assurances.map((a) => (
              <span
                key={a.label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#334155",
                  background: "rgba(255,255,255,.85)",
                  border: "1px solid #E2E8F0",
                  borderRadius: 20,
                  padding: "8px 14px",
                }}
              >
                <span style={{ color: "#0055FF", display: "inline-flex" }}>{a.icon}</span>
                {a.label}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhyOnePass() {
  const features = [
    {
      icon: <Brain size={26} />,
      title: "Blueprint Intelligence",
      desc: "Turns raw ideas into structured, complete build specifications automatically.",
    },
    {
      icon: <ShieldCheck size={26} />,
      title: "Expert Validation",
      desc: "Detects missing logic, conflicts, and gaps before a single line of code.",
    },
    {
      icon: <Lock size={26} />,
      title: "Build Protection Gate",
      desc: "Nothing ships to your coding tools until the blueprint is locked and verified.",
    },
    {
      icon: <Boxes size={26} />,
      title: "Complete Blueprint Package",
      desc: "One finalized, validated blueprint delivered in the formats your entire team needs.",
    },
    {
      icon: <Globe size={26} />,
      title: "Privacy First",
      desc: "Your ideas stay yours. No registration, no data harvesting, ever.",
    },
    {
      icon: <Upload size={26} />,
      title: "Export Anywhere",
      desc: "One-click export to Cursor, Claude, Codex, and any AI coding platform.",
    },
  ];
  return (
    <section
      id="why-onepass"
      style={{
        padding: "80px 0",
        background: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        className="op-blob op-blob-b"
        style={{
          width: 400,
          height: 400,
          top: -160,
          right: -160,
          background: "#00B6FF",
          opacity: 0.15,
        }}
      />
      <div className="op-container" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="Why OnePass"
          title="Everything you need to build with confidence"
        />
        <div
          className="op-features-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 22,
          }}
        >
          {features.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 120}>
              <div className="op-feature">
                <div className="op-feature-icon">{f.icon}</div>
                <div>
                  <h3
                    style={{
                      fontSize: 19,
                      fontWeight: 700,
                      color: "#001645",
                      margin: "20px 0 10px",
                    }}
                  >
                    {f.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 14.5,
                      color: "#5a6a8a",
                      lineHeight: 1.65,
                      margin: 0,
                    }}
                  >
                    {f.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Audience Block: Built for Everyone Involved in the Build */}
        <Reveal delay={250}>
          <div
            style={{
              marginTop: 48,
              background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
              border: "1px solid #E5E7EB",
              borderRadius: 24,
              padding: "36px 32px",
              boxShadow: "0 8px 30px rgba(0,22,69,.04)",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#0055FF",
                  background: "rgba(0,85,255,.08)",
                  padding: "4px 12px",
                  borderRadius: 999,
                  marginBottom: 8,
                }}
              >
                <Users size={13} /> Audience
              </span>
              <h3
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#001645",
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                Built for Everyone Involved in the Build
              </h3>
            </div>

            <div
              className="op-audience-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 16,
              }}
            >
              {[
                {
                  icon: <Brain size={18} color="#0055FF" />,
                  title: "Founders & Business Owners",
                  desc: "understand and approve what is being built.",
                },
                {
                  icon: <ShieldCheck size={18} color="#0D9488" />,
                  title: "Executives, Clients & Compliance",
                  desc: "review the project without reading code or JSON.",
                },
                {
                  icon: <Users size={18} color="#8B5CF6" />,
                  title: "Product & IT Teams",
                  desc: "work from the same requirements, workflows and acceptance criteria.",
                },
                {
                  icon: <Terminal size={18} color="#16a34a" />,
                  title: "Developers & Software Agencies",
                  desc: "receive the validated Ready-for-Coding blueprint.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  style={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 16,
                    padding: "18px 20px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    transition: "transform .2s, box-shadow .2s, border-color .2s",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "#F1F5F9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#001645",
                        marginBottom: 4,
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: "#5a6a8a",
                        lineHeight: 1.5,
                      }}
                    >
                      — {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function InteractiveDemo() {
  const { ref, visible } = useReveal(0.3);
  const [phase, setPhase] = useState(0); // 0 typing, 1 blueprint, 2 validate, 3 lock, 4 export
  const [typed, setTyped] = useState("");
  const fullText =
    "Build a customer portal with auth, billing and a usage dashboard...";

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const run = async () => {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      setPhase(0);
      setTyped("");
      for (let i = 1; i <= fullText.length; i++) {
        if (cancelled) return;
        setTyped(fullText.slice(0, i));
        await sleep(34);
      }
      await sleep(500);
      if (cancelled) return;
      setPhase(1);
      await sleep(1600);
      if (cancelled) return;
      setPhase(2);
      await sleep(1800);
      if (cancelled) return;
      setPhase(3);
      await sleep(1400);
      if (cancelled) return;
      setPhase(4);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const checks = ["Logic complete", "No conflicts", "Architecture valid"];

  return (
    <section
      id="features"
      style={{ padding: "80px 0", position: "relative", overflow: "hidden" }}
    >
      <div
        className="op-blob op-blob-a"
        style={{
          width: 460,
          height: 460,
          bottom: -200,
          left: -180,
          background: "#0055FF",
          opacity: 0.18,
        }}
      />
      <div className="op-container" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="See it in action"
          title="Watch a blueprint come to life"
          subtitle="From messy idea to locked, export-ready blueprint — automatically."
        />
        <Reveal>
          <div
            ref={ref}
            className="op-glass"
            style={{
              maxWidth: 860,
              margin: "0 auto",
              overflow: "hidden",
              borderRadius: 24,
            }}
          >
            {/* browser chrome */}
            <div
              className="op-demo-chrome"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "14px 20px",
                borderBottom: "1px solid #E5E7EB",
                background: "rgba(248,250,252,.8)",
              }}
            >
              {["#fca5a5", "#fcd34d", "#86efac"].map((c) => (
                <span
                  key={c}
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: "50%",
                    background: c,
                    flexShrink: 0,
                  }}
                />
              ))}
              <span
                style={{
                  marginLeft: 14,
                  fontSize: 12.5,
                  color: "#8a9ab8",
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  padding: "5px 14px",
                  flex: 1,
                  maxWidth: 300,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                onepass.app/builder
              </span>
            </div>
            <div
              className="op-demo-body"
              style={{
                padding: "36px 40px",
                minHeight: 340,
                background: "#fff",
              }}
            >
              {/* input */}
              <div
                style={{
                  border: "1.5px solid",
                  borderColor: phase === 0 ? "#0055FF" : "#E5E7EB",
                  borderRadius: 14,
                  padding: "16px 18px",
                  fontSize: 14.5,
                  color: "#334155",
                  background: "#F8FAFC",
                  transition: "border-color .4s",
                  boxShadow:
                    phase === 0 ? "0 0 0 4px rgba(0,85,255,.08)" : "none",
                }}
              >
                {typed}
                {phase === 0 && <span className="op-caret" />}
              </div>

              {/* blueprint */}
              {phase >= 1 && (
                <div
                  className="op-pop op-demo-blueprint"
                  style={{
                    marginTop: 20,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 12,
                  }}
                >
                  {["Auth Module", "Billing Flow", "Usage Dashboard"].map(
                    (m, i) => (
                      <div
                        key={m}
                        className="op-pop"
                        style={{
                          animationDelay: `${i * 0.15}s`,
                          border: "1px solid #E5E7EB",
                          borderRadius: 14,
                          padding: "14px 16px",
                          background: "#F8FAFC",
                        }}
                      >
                        <Layers size={16} color="#0055FF" />
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            marginTop: 8,
                            color: "#001645",
                          }}
                        >
                          {m}
                        </div>
                        <div
                          style={{
                            height: 5,
                            borderRadius: 3,
                            background: "#e2eafb",
                            marginTop: 8,
                            width: "85%",
                          }}
                        />
                        <div
                          style={{
                            height: 5,
                            borderRadius: 3,
                            background: "#edf2fb",
                            marginTop: 5,
                            width: "60%",
                          }}
                        />
                      </div>
                    ),
                  )}
                </div>
              )}

              {/* validation */}
              {phase >= 2 && (
                <div
                  style={{
                    marginTop: 18,
                    display: "flex",
                    gap: 14,
                    flexWrap: "wrap",
                  }}
                >
                  {checks.map((c, i) => (
                    <span
                      key={c}
                      className="op-pop"
                      style={{
                        animationDelay: `${i * 0.25}s`,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#16a34a",
                        background: "rgba(34,197,94,.1)",
                        padding: "7px 14px",
                        borderRadius: 999,
                      }}
                    >
                      <CheckCircle2 size={15} /> {c}
                    </span>
                  ))}
                </div>
              )}

              {/* lock + export status */}
              <div
                className="op-demo-actions"
                style={{
                  marginTop: 22,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  minHeight: 52,
                  flexWrap: "wrap",
                }}
              >
                {phase >= 3 && (
                  <span
                    className="op-pop"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0055FF",
                      background: "rgba(0,85,255,.08)",
                      padding: "12px 20px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,85,255,.2)",
                    }}
                  >
                    <Lock size={16} /> Blueprint Locked
                  </span>
                )}
                {phase >= 4 && (
                  <span
                    className="op-pop"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(93deg,#16a34a,#22C55E)",
                      padding: "12px 20px",
                      borderRadius: 12,
                      boxShadow: "0 8px 24px rgba(34,197,94,.35)",
                    }}
                  >
                    <Sparkles size={16} /> Complete Blueprint Package Ready ✓
                  </span>
                )}
              </div>

              {/* Phase 4 Live Deliverables Preview */}
              {phase >= 4 && (
                <div
                  className="op-pop"
                  style={{
                    marginTop: 18,
                    background: "linear-gradient(135deg, rgba(0,85,255,.04), rgba(0,182,255,.08))",
                    border: "1px solid rgba(0,85,255,.18)",
                    borderRadius: 16,
                    padding: "16px 18px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#001645",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Boxes size={15} color="#0055FF" /> Complete Blueprint Package
                    </span>
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: "#16a34a",
                        background: "rgba(34,197,94,.12)",
                        padding: "3px 10px",
                        borderRadius: 999,
                      }}
                    >
                      Multi-Format Export
                    </span>
                  </div>
                  <div
                    className="op-package-grid"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#0055FF",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Users size={12} /> For People
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                          <FileText size={13} color="#0055FF" /> Human-Readable Blueprint
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                          <FileSpreadsheet size={13} color="#D97706" /> Executive Summary PDF
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                          <Layers size={13} color="#8B5CF6" /> Visual Diagrams
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#16a34a",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Terminal size={12} /> For Development
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                        <Code size={13} color="#16a34a" /> Ready-for-Coding JSON
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, lineHeight: 1.4 }}>
                        Schema-strict JSON validated for AI coding agents &amp; IDEs.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Reveal>

        {/* Complete Blueprint Package Dedicated Feature Showcase */}
        <Reveal delay={200}>
          <div
            style={{
              maxWidth: 860,
              margin: "36px auto 0",
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 24,
              padding: "36px 32px",
              boxShadow: "0 16px 48px rgba(0,22,69,.06)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: "linear-gradient(90deg, #00B6FF, #0055FF, #22C55E)",
              }}
            />

            <div style={{ marginBottom: 28 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#0055FF",
                  background: "rgba(0,85,255,.08)",
                  padding: "4px 12px",
                  borderRadius: 999,
                  marginBottom: 10,
                }}
              >
                <Boxes size={14} /> Core Feature
              </div>
              <h3
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: "#001645",
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                Complete Blueprint Package
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: "#5a6a8a",
                  margin: "8px 0 0",
                  lineHeight: 1.6,
                  maxWidth: 680,
                }}
              >
                One finalized, validated blueprint delivered in the formats your entire team needs.
              </p>
            </div>

            <div
              className="op-package-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              {/* For People Card */}
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 18,
                  padding: "24px 22px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: "rgba(0,85,255,.12)",
                        color: "#0055FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Users size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#001645" }}>
                        For People
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Stakeholder alignment &amp; executive clarity
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <FileText size={16} color="#0055FF" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#001645" }}>
                          Human-Readable Blueprint
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          Clear specifications, user flows &amp; acceptance criteria.
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <FileSpreadsheet size={16} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#001645" }}>
                          Executive Summary PDF
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          High-level overview, milestone plan &amp; scope summary.
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <Layers size={16} color="#8B5CF6" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#001645" }}>
                          Visual Diagrams
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          System architecture flows &amp; UI structure maps.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* For Development Card */}
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 18,
                  padding: "24px 22px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: "rgba(34,197,94,.12)",
                        color: "#16a34a",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Terminal size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#001645" }}>
                        For Development
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Direct ingestion for AI agents &amp; IDEs
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 12,
                        padding: "12px 14px",
                      }}
                    >
                      <Code size={18} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#001645" }}>
                          Ready-for-Coding JSON
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, lineHeight: 1.45 }}>
                          Strict schema-validated structure containing data contracts, route definitions, and state schemas tailored for Cursor, Claude, Codex, and coding tools.
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        background: "#001645",
                        borderRadius: 12,
                        padding: "12px 14px",
                        fontFamily: "monospace",
                        fontSize: 11.5,
                        color: "#93C5FD",
                        overflowX: "auto",
                        whiteSpace: "pre",
                        lineHeight: 1.5,
                      }}
                    >
{`{
  "blueprint_version": "2.0",
  "status": "validated",
  "build_ready": true,
  "targets": ["cursor", "claude", "codex"]
}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BuildProtection() {
  const { ref, visible } = useReveal(0.3);
  const items = [
    "Missing Logic",
    "Conflict Detection",
    "Architecture Validation",
    "Requirement Completeness",
    "Security Checks",
  ];
  return (
    <section
      style={{
        padding: "96px 0",
        background: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        className="op-container op-split"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: 72,
          alignItems: "center",
        }}
      >
        {/* shield illustration */}
        <Reveal>
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "8%",
                background:
                  "radial-gradient(circle,rgba(0,182,255,.3),transparent 70%)",
                filter: "blur(60px)",
              }}
            />
            <div
              className="op-floating"
              style={{
                position: "relative",
                width: 260,
                height: 300,
                borderRadius: 40,
                background:
                  "linear-gradient(145deg,#00B6FF 0%,#0052D4 55%,#001A4A 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 32px 80px rgba(0,85,255,.4)",
                clipPath:
                  "path('M130 0 L260 50 V170 Q260 260 130 300 Q0 260 0 170 V50 Z')",
              }}
            >
              <ShieldCheck size={110} color="#fff" strokeWidth={1.4} />
            </div>
            <div
              className="op-glass op-floating-s"
              style={{
                position: "absolute",
                top: 20,
                right: "8%",
                padding: "10px 16px",
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              <CheckCircle2 size={16} color="#22C55E" /> 12 issues caught
            </div>
            <div
              className="op-glass op-floating-s"
              style={{
                position: "absolute",
                bottom: 8,
                left: "6%",
                padding: "10px 16px",
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12.5,
                fontWeight: 700,
                animationDelay: "-3s",
              }}
            >
              <Zap size={16} color="#0055FF" /> Fixed pre-build
            </div>
          </div>
        </Reveal>
        {/* checklist */}
        <div ref={ref}>
          <Reveal>
            <h2
              style={{
                fontSize: 40,
                fontWeight: 800,
                letterSpacing: "-0.025em",
                color: "#001645",
                lineHeight: 1.15,
                margin: 0,
              }}
            >
              Stop expensive build mistakes before they happen.
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "#5a6a8a",
                margin: "18px 0 34px",
                lineHeight: 1.6,
              }}
            >
              OnePass runs every blueprint through a protection gate that
              catches what humans miss.
            </p>
          </Reveal>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {items.map((item, i) => (
              <div
                key={item}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: "#F8FAFC",
                  border: "1px solid #E5E7EB",
                  borderRadius: 16,
                  padding: "16px 20px",
                  opacity: visible ? 1 : 0,
                  animation: visible
                    ? `op-check-in .6s cubic-bezier(.22,1,.36,1) ${i * 0.15}s both`
                    : "none",
                }}
              >
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "rgba(34,197,94,.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Check size={16} color="#22C55E" strokeWidth={3} />
                </span>
                <span
                  style={{ fontWeight: 600, fontSize: 15.5, color: "#001645" }}
                >
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Statistics() {
  const { ref, visible } = useReveal(0.4);
  const stats = [
    { value: 99, suffix: "%", label: "Blueprint Accuracy" },
    { value: 70, suffix: "%", label: "Less Build Waste" },
    { value: 3, suffix: "x", label: "Faster Planning" },
    { value: 100, suffix: "%", label: "Privacy First" },
  ];
  return (
    <section
      style={{ padding: "88px 0", position: "relative", overflow: "hidden" }}
    >
      <div className="op-container">
        <div
          ref={ref}
          className="op-grad-anim"
          style={{
            borderRadius: 28,
            padding: "64px 40px",
            background:
              "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
            boxShadow: "0 32px 80px rgba(0,85,255,.35)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(0,182,255,.3), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,.08), transparent 40%)",
            }}
          />
          <div
            className="op-stats-grid"
            style={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 32,
              textAlign: "center",
            }}
          >
            {stats.map((s) => (
              <StatItem key={s.label} {...s} start={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatItem({ value, suffix, label, start }) {
  const n = useCountUp(value, start);
  return (
    <div>
      <div
        style={{
          fontSize: 52,
          fontWeight: 800,
          color: "#fff",
          letterSpacing: "-0.03em",
          lineHeight: 1,
        }}
      >
        {n}
        <span style={{ color: "#7dd8ff" }}>{suffix}</span>
      </div>
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 500,
          color: "rgba(255,255,255,.75)",
          marginTop: 10,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Testimonials() {
  const quotes = [
    {
      quote:
        "OnePass caught three architecture conflicts our team completely missed. It paid for itself before we wrote a single prompt.",
      name: "Sarah Kim",
      role: "CTO, Northwind Labs",
      initials: "SK",
    },
    {
      quote:
        "We stopped burning tokens on half-baked specs. Every build now starts from a locked blueprint. Game changer.",
      name: "Marcus Chen",
      role: "Head of Product, Vantage",
      initials: "MC",
    },
    {
      quote:
        "The validation gate feels like having a senior architect review every idea before it reaches Cursor. Effortless.",
      name: "Elena Rossi",
      role: "Founder, Brightline",
      initials: "ER",
    },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % quotes.length), 5000);
    return () => clearInterval(t);
  }, [quotes.length]);

  return (
    <section style={{ padding: "96px 0", background: "#fff" }}>
      <div className="op-container">
        <SectionHeading
          eyebrow="Testimonials"
          title="Loved by teams that ship"
        />
        <div style={{ maxWidth: 720, margin: "0 auto", position: "relative" }}>
          <div
            className="op-glass"
            style={{
              borderRadius: 24,
              padding: "48px 52px",
              minHeight: 280,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 18,
                left: 32,
                fontSize: 110,
                fontWeight: 800,
                color: "rgba(0,85,255,.08)",
                lineHeight: 1,
                fontFamily: "Georgia,serif",
              }}
            >
              "
            </span>
            {quotes.map((q, i) => (
              <div
                key={q.name}
                style={{
                  position: i === idx ? "relative" : "absolute",
                  inset: i === idx ? undefined : "48px 52px",
                  opacity: i === idx ? 1 : 0,
                  transform: i === idx ? "translateY(0)" : "translateY(16px)",
                  transition: "opacity .6s ease, transform .6s ease",
                  pointerEvents: i === idx ? "auto" : "none",
                }}
              >
                <p
                  style={{
                    fontSize: 20,
                    lineHeight: 1.6,
                    color: "#1e2a4a",
                    fontWeight: 500,
                    margin: "16px 0 30px",
                    position: "relative",
                  }}
                >
                  {q.quote}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      background: "linear-gradient(145deg,#00B6FF,#0052D4)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    {q.initials}
                  </span>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#001645",
                      }}
                    >
                      {q.name}
                    </div>
                    <div style={{ fontSize: 13, color: "#7a8aa8" }}>
                      {q.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 8,
              marginTop: 24,
            }}
          >
            {quotes.map((_, i) => (
              <button
                key={i}
                aria-label={`Testimonial ${i + 1}`}
                className={`op-dot ${i === idx ? "active" : ""}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const gridRef = useRef(null);
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({ name: "", email: "", phone: "" });
  const [quoteFormErrors, setQuoteFormErrors] = useState({});
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  const handlePricingClick = async (planId) => {
    if (planId === 'enterprise') {
      setQuoteFormOpen(true);
      return;
    }

    const token = getToken();
    if (!token) {
      navigate('/build/login');
      return;
    }

    if (planId === 'free') {
      navigate('/build');
      return;
    }

    setLoadingPlan(planId);
    setIsRedirecting(true);
    try {
      const profile = await getMe();
      const productType = 'credit_pack';
      const res = await createCheckoutSession(profile.email, productType);
      window.location.href = res.url;
    } catch (err) {
      console.error("Checkout error:", err);
      // Show what actually went wrong rather than a blanket failure — a
      // generic message here hides the real reason from both users and us.
      alert(
        err?.response?.data?.detail ||
          "We could not start checkout. Please try again in a moment.",
      );
      setLoadingPlan(null);
      setIsRedirecting(false);
    }
  };

  // Tracks the user's cursor position dynamically to drive the sleek, fluid hover lighting matrix 
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!gridRef.current) return;
      const cards = gridRef.current.querySelectorAll('.pricing-card');
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty('--mouse-x', `${x}px`);
        card.style.setProperty('--mouse-y', `${y}px`);
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const plans = [
    {
      id: 'free',
      title: 'Free Blueprint Trial',
      price: '$0',
      period: '',
      description: 'Try OnePass with 3 free blueprint attempts.',
      features: ['3 Blueprint Generations', 'Blueprint Validation', 'Build Protection', 'No Credit Card Required'],
      buttonText: 'Start Free',
      buttonVariant: 'outline',
      popular: false
    },
    {
      id: 'popular',
      title: 'Blueprint Build Credit Pack',
      price: '$5',
      period: '10 Build Credits',
      description: 'Get 10 Build Credits for additional blueprints.',
      features: ['10 Build Credits', 'Fast Processing', 'Save Progress', 'Priority Queue'],
      buttonText: 'Buy Credits',
      buttonVariant: 'gradient',
      popular: true
    },

    {
      id: 'enterprise',
      title: 'Custom / Enterprise Quote',
      price: 'Custom',
      period: '',
      description: 'For larger, advanced, or implementation-heavy projects.',
      features: ['Enterprise Support', 'Custom Workflows', 'Team Collaboration', 'Dedicated Assistance'],
      buttonText: 'Contact Sales',
      buttonVariant: 'primary',
      popular: false
    }
  ];

  return (
    <section id="pricing" className="pricing-section">
      <div className="pricing-container">

        {/* Header Section */}
        <header className="pricing-header">

          <h2 className="pricing-title">Choose the plan that fits your project.</h2>
          <p className="pricing-subtitle">
            Start free, purchase build credits when needed, or let OnePass generate implementation-ready code for production projects.
          </p>
        </header>

        {/* Pricing Layout Matrix */}
        <div className="pricing-grid" ref={gridRef}>
          {plans.map((plan, index) => (
            <article
              key={plan.id}
              className={`pricing-card ${plan.popular ? 'is-popular' : ''}`}
              style={{ '--card-index': index }}
            >
              {plan.popular && (
                <div className="popular-badge" aria-label="Most Popular Plan">
                  MOST POPULAR
                </div>
              )}

              <div className="card-content">
                <h3 className="card-title">{plan.title}</h3>

                <div className="card-price-container">
                  <span className="card-price">{plan.price}</span>
                  {plan.period && <span className="card-period">{plan.period}</span>}
                </div>

                <p className="card-description">{plan.description}</p>

                <hr className="card-divider" />

                <ul className="card-features" aria-label={`Features for ${plan.title}`}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <div className="icon-wrapper">
                        <svg
                          className="feature-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                className={`card-button btn-${plan.buttonVariant}`}
                onClick={() => handlePricingClick(plan.id)}
                disabled={isRedirecting && loadingPlan === plan.id}
              >
                <span className="btn-text">
                  {isRedirecting && loadingPlan === plan.id ? "Redirecting..." : plan.buttonText}
                </span>
              </button>
            </article>
          ))}
        </div>

      </div>

      {/* Custom Quote Form Modal */}
      <Dialog open={quoteFormOpen} onClose={() => setQuoteFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a Custom Quote</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please fill out the details below to request a custom quote.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={quoteFormData.name}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, name: e.target.value });
              if (quoteFormErrors.name) setQuoteFormErrors({ ...quoteFormErrors, name: "" });
            }}
            error={!!quoteFormErrors.name}
            helperText={quoteFormErrors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={quoteFormData.email}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, email: e.target.value });
              if (quoteFormErrors.email) setQuoteFormErrors({ ...quoteFormErrors, email: "" });
            }}
            error={!!quoteFormErrors.email}
            helperText={quoteFormErrors.email}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={quoteFormData.phone}
            onChange={(e) => {
              setQuoteFormData({ ...quoteFormData, phone: e.target.value });
              if (quoteFormErrors.phone) setQuoteFormErrors({ ...quoteFormErrors, phone: "" });
            }}
            error={!!quoteFormErrors.phone}
            helperText={quoteFormErrors.phone}
            placeholder="+1 (555) 000-0000"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setQuoteFormOpen(false)} disabled={isSubmittingQuote}>
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              const errors = {};
              if (!quoteFormData.name.trim()) errors.name = "Name is required";
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!quoteFormData.email.trim()) {
                errors.email = "Email is required";
              } else if (!emailRegex.test(quoteFormData.email)) {
                errors.email = "Invalid email format";
              }
              const phoneRegex = /^\+?[\d\s\-\(\)]{7,20}$/;
              if (!quoteFormData.phone.trim()) {
                errors.phone = "Phone number is required";
              } else if (!phoneRegex.test(quoteFormData.phone)) {
                errors.phone = "Invalid phone number format";
              }

              if (Object.keys(errors).length > 0) {
                setQuoteFormErrors(errors);
                return;
              }

              setIsSubmittingQuote(true);
              try {
                await submitQuoteRequest({
                  name: quoteFormData.name.trim(),
                  email: quoteFormData.email.trim(),
                  phone: quoteFormData.phone.trim(),
                  query: "Requested from Landing Page",
                  blueprint: "N/A",
                });
                setQuoteFormOpen(false);
                setQuoteFormData({ name: "", email: "", phone: "" });
                alert("Quote request submitted successfully!");
              } catch (err) {
                alert(
                  err?.response?.data?.detail ||
                  "Failed to submit quote request. Please try again."
                );
              } finally {
                setIsSubmittingQuote(false);
              }
            }}
            disabled={isSubmittingQuote}
          >
            {isSubmittingQuote ? <CircularProgress size={24} /> : "Submit Request"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </section>
  );
}

function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section id="get-started" style={{ padding: "88px 0 96px" }}>
      <div className="op-container">
        <Reveal>
          <div
            className="op-grad-anim op-final-cta-box"
            style={{
              borderRadius: 32,
              padding: "88px 40px",
              textAlign: "center",
              background:
                "linear-gradient(93deg,#0D47A1 0%,#0055FF 50%,#001645 100%)",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 40px 100px rgba(0,85,255,.4)",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle at 30% 20%, rgba(0,182,255,.35), transparent 50%), radial-gradient(circle at 75% 80%, rgba(255,255,255,.1), transparent 45%)",
              }}
            />
            <div style={{ position: "relative" }}>
              <h2
                className="op-final-cta-h2"
                style={{
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#fff",
                  letterSpacing: "-0.03em",
                  margin: 0,
                  lineHeight: 1.1,
                }}
              >
                Ready to build smarter?
              </h2>
              <p
                className="op-final-cta-p"
                style={{
                  fontSize: 18,
                  color: "rgba(255,255,255,.8)",
                  margin: "20px auto 40px",
                  maxWidth: 480,
                  lineHeight: 1.6,
                }}
              >
                Create validated blueprints before writing a single line of
                code.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <RippleButton
                  onClick={() => navigate("/build")}
                  className="op-btn-primary"
                  style={{
                    padding: "19px 40px",
                    fontSize: 17,
                    background: "#fff",
                    color: "#0055FF",
                    boxShadow: "0 12px 36px rgba(0,0,0,.25)",
                  }}
                >
                  Try OnePass Free <ArrowRight size={19} />
                </RippleButton>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}



// function Footer() {
//   const cols = {
//     Product: ["Features", "Pricing", "Changelog", "Roadmap"],
//     // Resources: ["Docs", "Blog", "Guides", "Support"],
//     // Company: ["About", "Careers", "Contact"],
//     // Legal: ["Privacy", "Terms", "Security"],
//   };
//   return (
//     <footer style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "64px 0 40px" }}>
//       <div className="op-container">
//         <div className="op-footer-grid" style={{ display: "grid", gridTemplateColumns: "5fr 1fr ", gap: 40, marginBottom: 48 }}>
//           <div>
//             <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
//               <LogoMark />
//               <span style={{ fontWeight: 800, fontSize: 19, color: "#001645" }}>OnePass</span>
//             </span>
//             <p style={{ fontSize: 13.5, color: "#7a8aa8", marginTop: 16, lineHeight: 1.6, maxWidth: 240 }}>
//               Validated build blueprints for the AI coding era.
//             </p>
//             <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
//             </div>
//           </div>
//           {Object.entries(cols).map(([title, links]) => (
//             <div key={title}>
//               <div style={{ fontSize: 13, fontWeight: 700, color: "#001645", marginBottom: 16, letterSpacing: "0.04em" }}>{title}</div>
//               {links.map((l) => (
//                 <a key={l} href="#" style={{ display: "block", fontSize: 13.5, color: "#7a8aa8", textDecoration: "none", marginBottom: 11 }}>
//                   {l}
//                 </a>
//               ))}
//             </div>
//           ))}
//         </div>
//         <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 26, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
//           <span style={{ fontSize: 13, color: "#9aa8c4" }}>© 2026 OnePass. All rights reserved.</span>
//           <span style={{ fontSize: 13, color: "#9aa8c4" }}>Fix it before you build it.</span>
//         </div>
//       </div>
//     </footer>
//   );
// }

function DisclaimerSection() {
  const [open, setOpen] = useState(null);

  const sections = [
    {
      id: "free-trial",
      title: "Free Trial",
      content: (
        <>
          <p>Free-trial users can create up to three eligible blueprints during a 30-day period without registering for an account. The trial and all resulting outputs are provided on an <em>"as-is"</em> and <em>"as-available"</em> basis, without warranties or guarantees regarding accuracy, completeness, availability, or suitability for any particular purpose.</p>
        </>
      ),
    },
    {
      id: "ai-disclosure",
      title: "Artificial Intelligence Disclosure",
      content: (
        <>
          <p>OnePass uses artificial intelligence and automated validation tools to generate, analyze, and review blueprints and related outputs. Although the service is designed to improve accuracy and identify potential issues, AI-generated and automated outputs may contain errors, omissions, or unexpected results.</p>
          <p style={{ marginTop: 10 }}>You are responsible for reviewing, testing, validating, and approving all outputs before using them for development, production, business operations, or any other purpose. OnePass does not provide legal, financial, medical, engineering, or other licensed professional advice.</p>
        </>
      ),
    },
    {
      id: "accounts-payments",
      title: "Accounts and Payments",
      content: (
        <>
          <p>No account registration or billing information is required to use the free trial.</p>
          <p style={{ marginTop: 10 }}>Users who purchase Blueprint Credits or other paid services must create an account and provide limited information necessary to register and manage the account, such as their name and contact information. A $5 payment provides the number of Blueprint Credits displayed at the time of purchase.</p>
          <p style={{ marginTop: 10 }}>Payment and credit-card information is securely collected and processed by an independent third-party payment provider and is subject to that provider's terms and privacy practices. OnePass does not directly store your complete credit-card number or card security code.</p>
          <p style={{ marginTop: 10 }}>We may retain limited account and transaction information necessary to manage your account, credits, purchases, customer support, platform security, and legal or recordkeeping obligations.</p>
        </>
      ),
    },
    {
      id: "submitted-content",
      title: "Submitted Content and Privacy",
      content: (
        <>
          <p>The service processes the instructions, specifications, files, and other content you voluntarily submit to generate the requested output and to operate, secure, and support the platform.</p>
          <p style={{ marginTop: 10 }}>We do not retain submitted project content longer than reasonably necessary to provide the requested service, maintain security, prevent misuse, resolve technical issues, or comply with applicable legal obligations.</p>
          <p style={{ marginTop: 10 }}>You are responsible for ensuring that you have the right and authorization to submit all content provided to the service. Do not submit highly sensitive, confidential, regulated, or personally identifiable information unless you are authorized to do so and the service has been approved for that type of information.</p>
          <p style={{ marginTop: 10 }}>Limited information may be processed by third-party service providers that support payment processing, account management, hosting, security, artificial intelligence, and operation of the service.</p>
          <p style={{ marginTop: 10 }}>We do not sell personal information or share it with third parties for their independent advertising purposes.</p>
        </>
      ),
    },
  ];

  return (
    <section
      id="legal"
      style={{
        background: "#F8FAFC",
        borderTop: "1px solid #E5E7EB",
        padding: "64px 0 56px",
      }}
    >
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px" }}>
        {/* Heading */}
        <div style={{ marginBottom: 36, textAlign: "center" }}>
          <span
            style={{
              display: "inline-block",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#0055FF",
              background: "rgba(0,85,255,.08)",
              borderRadius: 6,
              padding: "4px 12px",
              marginBottom: 14,
            }}
          >
            Legal &amp; Privacy
          </span>
          <h2
            style={{
              fontSize: 28,
              fontWeight: 800,
              color: "#001645",
              margin: "0 0 10px",
              letterSpacing: "-0.02em",
            }}
          >
            Disclaimer &amp; Privacy Notice
          </h2>
          <p style={{ color: "#667085", fontSize: 15, margin: 0 }}>
            By using the free trial, creating an account, or purchasing a paid service, you agree to the applicable Terms of Use and Privacy Policy.
          </p>
        </div>

        {/* Accordion */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sections.map((s) => {
            const isOpen = open === s.id;
            return (
              <div
                key={s.id}
                style={{
                  background: "#fff",
                  border: `1px solid ${isOpen ? "rgba(0,85,255,.3)" : "#E5E7EB"}`,
                  borderRadius: 14,
                  overflow: "hidden",
                  transition: "border-color .2s",
                  boxShadow: isOpen
                    ? "0 4px 20px rgba(0,85,255,.08)"
                    : "0 1px 4px rgba(0,22,69,.04)",
                }}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : s.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "18px 22px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    gap: 16,
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: isOpen ? "#0055FF" : "#001645",
                      transition: "color .2s",
                    }}
                  >
                    {s.title}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      width: 24,
                      height: 24,
                      borderRadius: 8,
                      background: isOpen ? "rgba(0,85,255,.1)" : "rgba(0,22,69,.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isOpen ? "#0055FF" : "#667085",
                      transition: "all .2s",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <ChevronDown size={15} />
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      padding: "16px 22px 20px",
                      color: "#4B5563",
                      fontSize: 14,
                      lineHeight: 1.75,
                      borderTop: "1px solid #F1F5F9",
                    }}
                  >
                    {s.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const links = [
    { label: "Features", to: "/#features" },
    { label: "Pricing", to: "/#pricing" },
    { label: "Terms of Service", to: "/terms-of-service" },
    { label: "Changelog", to: "#" },
    { label: "Roadmap", to: "#" },
  ];

  return (
    <footer
      style={{
        background: "#fff",
        borderTop: "1px solid #E5E7EB",
        padding: "40px 0 20px",
      }}
    >
      <div className="op-container">
        {/* Top */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 24,
            paddingBottom: 28,
          }}
        >
          <div>
            <div
              style={{
                display: "flex", alignItems: "start", gap: 4, marginBottom: 10, flexDirection: "column", 
              }}
            >
              <LogoMark size={80} />

              <div>
                {/* <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#001645",
                    lineHeight: 1.1,
                  }}
                >
                  OnePass
                </div> */}

                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0055FF",
                    marginTop: 4,
                    letterSpacing: "0.02em",
                  }}
                >
                  Powered by OnePassBlueprint.com
                </div>
              </div>
            </div>

            <p
              style={{
                color: "#667085",
                fontSize: 14,
                margin: 0,
              }}
            >
              Validated build blueprints for the AI coding era.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: 28,
              flexWrap: "wrap",
            }}
          >
            {links.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                style={{
                  color: "#667085",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div
          style={{
            borderTop: "1px solid #E5E7EB",
            paddingTop: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span
            style={{
              color: "#98A2B3",
              fontSize: 13,
            }}
          >
            © 2026 OnePass. All rights reserved.
          </span>

          <span
            style={{
              color: "#0055FF",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Fix it before you build it.
          </span>
        </div>
      </div>
    </footer>
  );
}

export default OnePassLanding;
